/**
 * Votações online — gestão (síndico/gestor) — 0.1.48.
 *
 * Cria a votação (pautas, janela, peso, sigilo, inadimplente), publica (dispara
 * e-mail aos moradores), acompanha o quórum e encerra — gerando a ata (PDF).
 *
 * 0.1.83: só o PROPRIETÁRIO vota; inquilino/morador vota com PROCURAÇÃO, que o síndico
 * registra aqui anexando o documento (painel "Procurações").
 *
 * 0.1.88: ENQUETE pré-assembleia — responde quem mora (proprietário, inquilino ou
 * morador), 1 resposta por apto, sem ata; o síndico vê o placar parcial e o morador vê
 * o resultado só ao encerrar.
 *
 * 0.1.88: ASSEMBLEIAS (painel no topo, `AssembleiasPainel.tsx`) — convocação com edital,
 * lista de presença com o logo, procuração e comprovante de envio.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, BarChart3, CalendarClock, CheckCircle2, FileSignature, FileText, Loader2, Plus, Send, Trash2, Vote, X,
} from "lucide-react";
import { useState } from "react";
import { api, ApiError, ataVotacaoUrl, procuracaoArquivoUrl } from "../lib/api";
import { AssembleiasGestao } from "./AssembleiasPainel";

interface Votacao {
  id: number; titulo: string; descricao: string | null; status: string;
  peso: string; sigilo: string; bloqueia_inadimplente: boolean;
  abre_em: string | null; fecha_em: string | null; encerrada_em: string | null;
  codigo_verificacao: string | null; aberta_agora: boolean; modalidade?: string;
}
interface ResultadoPautas {
  quorum: { unidades_votantes: number; unidades_total: number; percentual: number };
  pautas: { pergunta: string; vencedora: string | null; empate: boolean;
    por_opcao: { opcao: string; votos: number; percentual: number }[] }[];
}

const FORM0 = {
  titulo: "", descricao: "", fecha_em: "", modalidade: "VOTACAO",
  peso: "UNIDADE", sigilo: "ABERTO", bloqueia_inadimplente: true,
  pautas: [{ pergunta: "", opcoes: "Sim, Não, Abstenção" }],
};

// Modelos de opções (item 1): clicar preenche o campo. A lista continua livre —
// o síndico pode editar depois. Para eleição já sugerimos "Voto em branco" (item 3).
const MODELOS_OPCOES: { rotulo: string; opcoes: string }[] = [
  { rotulo: "Sim / Não / Abstenção", opcoes: "Sim, Não, Abstenção" },
  { rotulo: "Eleição (candidatos)", opcoes: "Candidato 1, Candidato 2, Voto em branco" },
  { rotulo: "Escolha de opções", opcoes: "Opção A, Opção B, Opção C" },
];

// Quebra "A, B, C" em ["A","B","C"] para a prévia em etiquetas (item 2).
function _opcoesLista(txt: string): string[] {
  return txt.split(",").map((o) => o.trim()).filter(Boolean);
}

export function VotacoesPage({ condominioId, onVoltar }: { condominioId: number; onVoltar: () => void }) {
  const qc = useQueryClient();
  const [aberto, setAberto] = useState(false);
  const [f, setF] = useState<typeof FORM0>({ ...FORM0, pautas: [{ ...FORM0.pautas[0] }] });
  const [erro, setErro] = useState<string | null>(null);

  const { data: lista } = useQuery<Votacao[]>({
    queryKey: ["votacoes", condominioId],
    queryFn: () => api.get(`/condominios/${condominioId}/votacoes`),
  });

  const criar = useMutation({
    mutationFn: () =>
      api.post(`/condominios/${condominioId}/votacoes`, {
        titulo: f.titulo.trim(), descricao: f.descricao.trim() || null,
        fecha_em: f.fecha_em ? new Date(f.fecha_em).toISOString() : null,
        peso: f.peso, sigilo: f.sigilo, bloqueia_inadimplente: f.bloqueia_inadimplente,
        modalidade: f.modalidade,
        pautas: f.pautas
          .filter((p) => p.pergunta.trim())
          .map((p) => ({
            pergunta: p.pergunta.trim(),
            opcoes: p.opcoes.split(",").map((o) => o.trim()).filter(Boolean),
          })),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["votacoes", condominioId] });
      setAberto(false); setF({ ...FORM0, pautas: [{ ...FORM0.pautas[0] }] }); setErro(null);
    },
    onError: (e) => setErro((e as Error).message || "Falha ao criar."),
  });

  const acao = useMutation({
    mutationFn: ({ id, tipo }: { id: number; tipo: "publicar" | "encerrar" }) =>
      api.post(`/votacoes/${id}/${tipo}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["votacoes", condominioId] }),
  });

  // Ajustar o prazo (encerra em) de uma votação já aberta, sem recriar.
  const [prazoId, setPrazoId] = useState<number | null>(null);
  const [placarId, setPlacarId] = useState<number | null>(null);   // 0.1.88: placar da enquete
  const [prazoVal, setPrazoVal] = useState("");
  const ajustarPrazo = useMutation({
    mutationFn: ({ id, fecha }: { id: number; fecha: string }) =>
      api.patch(`/votacoes/${id}/prazo`, { fecha_em: fecha ? new Date(fecha).toISOString() : null }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["votacoes", condominioId] });
      setPrazoId(null); setPrazoVal("");
    },
  });
  const excluir = useMutation({
    mutationFn: (id: number) => api.delete(`/votacoes/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["votacoes", condominioId] }),
  });

  // Toda pauta COM pergunta precisa de >=2 opções únicas (item 2/3).
  const pautasComPergunta = f.pautas.filter((p) => p.pergunta.trim());
  const pautasOk = pautasComPergunta.every((p) => {
    const ops = _opcoesLista(p.opcoes);
    return ops.length >= 2 && ops.length === new Set(ops.map((o) => o.toLowerCase())).size;
  });
  // "Encerra em" no passado deixaria a votação "fora do prazo" já ao abrir.
  const fechaNoPassado = !!f.fecha_em && new Date(f.fecha_em).getTime() < Date.now();
  const podeCriar = !!f.titulo.trim() && pautasComPergunta.length > 0 && pautasOk && !fechaNoPassado;
  const inp = "w-full text-sm rounded-md border border-slate-300 px-2.5 py-1.5";

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <div className="flex items-center gap-2 mb-4">
        <button onClick={onVoltar} className="inline-flex items-center gap-1.5 text-sm px-2.5 py-1.5 rounded-md border text-slate-600 bg-white border-slate-200 hover:bg-slate-50">
          <ArrowLeft size={16} /> Voltar
        </button>
        <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
          <Vote size={20} className="text-brand-700" /> Assembleias e votações
        </h1>
      </div>

      <AssembleiasGestao condominioId={condominioId}
        onVotacaoCriada={() => qc.invalidateQueries({ queryKey: ["votacoes", condominioId] })} />

      <h2 className="font-semibold text-slate-800 flex items-center gap-2 mb-2">
        <Vote size={17} className="text-brand-700" /> Votações e enquetes on-line
      </h2>

      {!aberto && (
        <button onClick={() => setAberto(true)}
          className="mb-4 inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-md bg-brand-700 text-white hover:brightness-110">
          <Plus size={16} /> Nova votação ou enquete
        </button>
      )}

      {aberto && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 mb-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold text-slate-800">{f.modalidade === "ENQUETE" ? "Nova enquete" : "Nova votação"}</h2>
            <button onClick={() => setAberto(false)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
          <div className="space-y-3">
            {/* 0.1.88: votação (assembleia, com ata) × enquete (pré-assembleia, só resultado) */}
            <div className="grid grid-cols-2 gap-2">
              {([["VOTACAO", "Votação", "Assembleia online. Vota o proprietário; gera ata."],
                 ["ENQUETE", "Enquete", "Consulta antes da assembleia. Só o resultado, sem ata."]] as const).map(([k, t, d]) => (
                <button key={k} type="button" onClick={() => setF({ ...f, modalidade: k })}
                  className={`text-left rounded-md border px-3 py-2 ${f.modalidade === k ? "border-brand-500 bg-brand-50" : "border-slate-200 bg-white"}`}>
                  <div className="text-sm font-semibold text-slate-800">{t}</div>
                  <div className="text-[11px] text-slate-500">{d}</div>
                </button>
              ))}
            </div>
            {f.modalidade === "ENQUETE" && (
              <p className="text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5">
                Responde <b>quem mora</b> (proprietário, inquilino ou morador), <b>1 resposta por
                apartamento</b>. Você acompanha o placar; os moradores veem o resultado só quando você
                encerrar. Não gera ata.
              </p>
            )}
            <div>
              <label className="text-xs text-slate-500">Título *</label>
              <input className={inp} value={f.titulo} onChange={(e) => setF({ ...f, titulo: e.target.value })}
                placeholder="Ex.: Aprovação da pintura da fachada" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Edital / contexto</label>
              <textarea className={`${inp} min-h-[70px]`} value={f.descricao}
                onChange={(e) => setF({ ...f, descricao: e.target.value })}
                placeholder="Instruções, orçamento em anexo, regras…" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="text-xs text-slate-500">Encerra em (opcional)</label>
                <input type="datetime-local" className={inp} value={f.fecha_em}
                  onChange={(e) => setF({ ...f, fecha_em: e.target.value })} />
                {fechaNoPassado ? (
                  <p className="text-[11px] text-rose-600 mt-0.5">Essa data já passou — a votação abriria "fora do prazo". Escolha uma data futura ou deixe em branco.</p>
                ) : (
                  <p className="text-[11px] text-slate-400 mt-0.5">Em branco = fica aberta até você encerrar.</p>
                )}
              </div>
              {f.modalidade !== "ENQUETE" && (
              <div>
                <label className="text-xs text-slate-500">Peso do voto</label>
                <select className={inp} value={f.peso} onChange={(e) => setF({ ...f, peso: e.target.value })}>
                  <option value="UNIDADE">1 unidade = 1 voto</option>
                  <option value="FRACAO">Por fração ideal</option>
                </select>
              </div>
              )}
              <div>
                <label className="text-xs text-slate-500">Sigilo</label>
                <select className={inp} value={f.sigilo} onChange={(e) => setF({ ...f, sigilo: e.target.value })}>
                  <option value="ABERTO">Aberto (nominal)</option>
                  <option value="SECRETO">Secreto (só o total)</option>
                </select>
              </div>
              {f.modalidade !== "ENQUETE" && (
              <div className="flex items-end">
                <label className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                  <input type="checkbox" checked={f.bloqueia_inadimplente}
                    onChange={(e) => setF({ ...f, bloqueia_inadimplente: e.target.checked })} />
                  Bloquear inadimplente
                </label>
              </div>
              )}
            </div>

            <div>
              <label className="text-xs text-slate-500">Pautas</label>
              {f.pautas.map((p, i) => {
                const opcoes = _opcoesLista(p.opcoes);
                const duplicadas = opcoes.length !== new Set(opcoes.map((o) => o.toLowerCase())).size;
                return (
                <div key={i} className="rounded-md border border-slate-200 p-2 mb-2">
                  <input className={`${inp} mb-1`} value={p.pergunta} placeholder={`Pergunta ${i + 1}`}
                    onChange={(e) => {
                      const ps = [...f.pautas]; ps[i] = { ...ps[i], pergunta: e.target.value }; setF({ ...f, pautas: ps });
                    }} />
                  {/* Item 1 — modelos: clicar troca as opções (lista continua editável). */}
                  <div className="flex flex-wrap gap-1 mb-1">
                    <span className="text-[11px] text-slate-400 self-center">Modelos:</span>
                    {MODELOS_OPCOES.map((m) => (
                      <button key={m.rotulo} type="button"
                        onClick={() => {
                          const ps = [...f.pautas]; ps[i] = { ...ps[i], opcoes: m.opcoes }; setF({ ...f, pautas: ps });
                        }}
                        className="text-[11px] px-2 py-0.5 rounded-full border border-slate-300 text-slate-600 hover:border-brand-400 hover:text-brand-700">
                        {m.rotulo}
                      </button>
                    ))}
                  </div>
                  <input className={inp} value={p.opcoes} placeholder="Opções separadas por vírgula (ex.: Maria, João, Voto em branco)"
                    onChange={(e) => {
                      const ps = [...f.pautas]; ps[i] = { ...ps[i], opcoes: e.target.value }; setF({ ...f, pautas: ps });
                    }} />
                  {/* Item 2 — prévia em etiquetas: o síndico confere antes de publicar. */}
                  {opcoes.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {opcoes.map((o, k) => (
                        <span key={k} className="text-[11px] px-2 py-0.5 rounded-md bg-brand-50 text-brand-800 border border-brand-100">{o}</span>
                      ))}
                    </div>
                  )}
                  {opcoes.length < 2 && (
                    <p className="text-[11px] text-amber-600 mt-1">Informe pelo menos 2 opções, separadas por vírgula.</p>
                  )}
                  {duplicadas && (
                    <p className="text-[11px] text-rose-600 mt-1">Há opções repetidas — cada opção deve ser única.</p>
                  )}
                  {f.pautas.length > 1 && (
                    <button onClick={() => setF({ ...f, pautas: f.pautas.filter((_, j) => j !== i) })}
                      className="mt-1 text-xs text-rose-600">remover pauta</button>
                  )}
                </div>
                );
              })}
              <button onClick={() => setF({ ...f, pautas: [...f.pautas, { pergunta: "", opcoes: "Sim, Não, Abstenção" }] })}
                className="text-xs text-brand-700 inline-flex items-center gap-1"><Plus size={13} /> adicionar pauta</button>
            </div>

            {erro && <p className="text-sm text-rose-600">{erro}</p>}
            <button disabled={!podeCriar || criar.isPending} onClick={() => criar.mutate()}
              className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded-md bg-brand-700 text-white disabled:opacity-50">
              {criar.isPending ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
              Salvar rascunho
            </button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {(lista ?? []).map((v) => (
          <div key={v.id} className="rounded-md border border-slate-200 bg-white px-3 py-2.5">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium text-slate-900">
                  {v.modalidade === "ENQUETE" && (
                    <span className="mr-1.5 align-middle text-[10px] font-bold text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5">ENQUETE</span>
                  )}
                  {v.titulo}
                </div>
                <div className="text-xs text-slate-500">
                  {v.status === "RASCUNHO" && "Rascunho"}
                  {v.status === "ABERTA" && (v.aberta_agora ? "Aberta — recebendo votos" : "Aberta (fora da janela)")}
                  {v.status === "ENCERRADA" && `Encerrada${v.codigo_verificacao ? ` · cód. ${v.codigo_verificacao}` : ""}`}
                  {v.modalidade === "ENQUETE" ? " · 1 resposta por apto" : <>{" · "}{v.peso === "FRACAO" ? "fração ideal" : "1un=1voto"}</>}
                  {" · "}{v.sigilo === "ABERTO" ? "aberto" : "secreto"}
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {v.status === "RASCUNHO" && (
                  <button onClick={() => acao.mutate({ id: v.id, tipo: "publicar" })}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-brand-700 text-white">
                    <Send size={13} /> Publicar
                  </button>
                )}
                {v.status === "ABERTA" && (
                  <button onClick={() => { setPrazoId(prazoId === v.id ? null : v.id); setPrazoVal(""); }}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-700">
                    <CalendarClock size={13} /> Prazo
                  </button>
                )}
                {v.status === "ABERTA" && (
                  <button onClick={() => { if (confirm("Encerrar e apurar? Não dá para reabrir.")) acao.mutate({ id: v.id, tipo: "encerrar" }); }}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-amber-600 text-white">
                    <CheckCircle2 size={13} /> Encerrar
                  </button>
                )}
                {v.modalidade === "ENQUETE" && v.status !== "RASCUNHO" && (
                  <button onClick={() => setPlacarId(placarId === v.id ? null : v.id)}
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-violet-300 text-violet-700">
                    <BarChart3 size={13} /> {v.status === "ABERTA" ? "Placar" : "Resultado"}
                  </button>
                )}
                {v.status === "ENCERRADA" && v.modalidade !== "ENQUETE" && (
                  <a href={ataVotacaoUrl(v.id)} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md border border-slate-300 text-slate-700">
                    <FileText size={13} /> Ata
                  </a>
                )}
                {v.status !== "ENCERRADA" && (
                  <button onClick={() => { if (confirm("Excluir esta votação?")) excluir.mutate(v.id); }}
                    className="text-slate-400 hover:text-rose-600"><Trash2 size={15} /></button>
                )}
              </div>
            </div>
            {placarId === v.id && <PlacarEnquete votacao={v} />}
            {prazoId === v.id && (
              <div className="mt-2 rounded-md bg-slate-50 border border-slate-200 p-2">
                <label className="text-xs text-slate-500">Novo "encerra em" (em branco = sem prazo)</label>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <input type="datetime-local" className={`${inp} max-w-[220px]`} value={prazoVal}
                    onChange={(e) => setPrazoVal(e.target.value)} />
                  <button onClick={() => ajustarPrazo.mutate({ id: v.id, fecha: prazoVal })}
                    disabled={ajustarPrazo.isPending}
                    className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-brand-700 text-white disabled:opacity-50">
                    {ajustarPrazo.isPending ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Salvar prazo
                  </button>
                  <button onClick={() => { setPrazoId(null); setPrazoVal(""); }}
                    className="text-xs px-2 py-1.5 text-slate-500">cancelar</button>
                </div>
              </div>
            )}
          </div>
        ))}
        {lista && lista.length === 0 && <p className="text-sm text-slate-500">Nenhuma votação ainda.</p>}
      </div>

      <ProcuracoesPainel condominioId={condominioId} votacoes={lista ?? []} />
    </div>
  );
}

/** Placar (enquete aberta, só para a gestão) ou resultado final (encerrada) — 0.1.88. */
function PlacarEnquete({ votacao }: { votacao: Votacao }) {
  const aberta = votacao.status === "ABERTA";
  const { data, isLoading, error } = useQuery<{ resultado: ResultadoPautas }>({
    queryKey: ["enquete-placar", votacao.id, votacao.status],
    queryFn: () => api.get(`/votacoes/${votacao.id}/${aberta ? "parcial" : "resultado"}`),
    refetchInterval: aberta ? 30000 : false,
  });
  if (isLoading) return <p className="mt-2 text-xs text-slate-500">Carregando…</p>;
  if (error || !data) return <p className="mt-2 text-xs text-rose-600">Não foi possível carregar o placar.</p>;
  const r = data.resultado;
  return (
    <div className="mt-2 rounded-md bg-violet-50/50 border border-violet-200 p-2.5 space-y-2">
      <div className="text-xs text-slate-600">
        {aberta ? "Placar parcial (só você vê)" : "Resultado final"} · {r.quorum.unidades_votantes}/{r.quorum.unidades_total} apartamentos responderam ({r.quorum.percentual}%)
      </div>
      {r.pautas.map((p, i) => (
        <div key={i} className="rounded-md bg-white border border-slate-200 p-2">
          <div className="text-sm font-medium text-slate-800 mb-1">{p.pergunta}</div>
          {p.por_opcao.map((o) => (
            <div key={o.opcao} className="text-xs text-slate-600">
              <div className="flex justify-between"><span>{o.opcao}</span><span>{o.votos} ({o.percentual}%)</span></div>
              <div className="h-1.5 rounded bg-slate-100"><div className="h-1.5 rounded bg-violet-500" style={{ width: `${o.percentual}%` }} /></div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

// =====================================================================
// Procurações (0.1.83)
// =====================================================================
interface MembroApto {
  id: number; nome: string; apto: string | null; relacao?: string | null; ativo: boolean;
}
interface Procuracao {
  id: number; unidade: string; procurador_id: number; procurador_nome: string;
  outorgante_nome: string; votacao_id: number | null; valida_ate: string | null;
  arquivo_nome: string; criado_por: string | null; criado_em: string | null;
  revogada_em: string | null; vigente: boolean;
}

function ProcuracoesPainel({ condominioId, votacoes }: { condominioId: number; votacoes: Votacao[] }) {
  const qc = useQueryClient();
  const chave = ["procuracoes", condominioId];
  const { data: procs } = useQuery<Procuracao[]>({
    queryKey: chave, queryFn: () => api.get(`/condominios/${condominioId}/procuracoes`),
  });
  const { data: equipe } = useQuery<{ equipe: MembroApto[] }>({
    queryKey: ["equipe", condominioId], queryFn: () => api.get(`/condominios/${condominioId}/equipe`),
  });
  const [aberto, setAberto] = useState(false);
  const [apto, setApto] = useState("");
  const [procuradorId, setProcuradorId] = useState("");
  const [outorgante, setOutorgante] = useState("");
  const [validade, setValidade] = useState<"votacao" | "data" | "revogar">("votacao");
  const [votacaoId, setVotacaoId] = useState("");
  const [ate, setAte] = useState("");
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const membros = (equipe?.equipe ?? []).filter((m) => m.apto && m.ativo);
  const aptos = [...new Set(membros.map((m) => m.apto as string))].sort((a, b) =>
    a.localeCompare(b, "pt-BR", { numeric: true }));
  const doApto = membros.filter((m) => m.apto === apto);
  const candidatos = doApto.filter((m) => m.relacao !== "proprietario");
  const donos = doApto.filter((m) => m.relacao === "proprietario");
  const votacoesAbertas = votacoes.filter((v) => v.status !== "ENCERRADA");

  const escolherApto = (a: string) => {
    setApto(a); setProcuradorId("");
    const dono = membros.find((m) => m.apto === a && m.relacao === "proprietario");
    setOutorgante(dono?.nome ?? "");
  };
  const criar = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append("arquivo", arquivo as File);
      fd.append("unidade", apto);
      fd.append("procurador_id", procuradorId);
      fd.append("outorgante_nome", outorgante);
      if (validade === "votacao") fd.append("votacao_id", votacaoId);
      if (validade === "data" && ate) fd.append("valida_ate", `${ate}T23:59:59-03:00`);
      return api.postForm(`/condominios/${condominioId}/procuracoes`, fd);
    },
    onSuccess: () => {
      setAberto(false); setApto(""); setProcuradorId(""); setOutorgante(""); setArquivo(null);
      setVotacaoId(""); setAte(""); setErro(null); qc.invalidateQueries({ queryKey: chave });
    },
    onError: (e) => setErro(e instanceof ApiError ? e.message : "Não foi possível registrar."),
  });
  const revogar = useMutation({
    mutationFn: (id: number) => api.post(`/procuracoes/${id}/revogar`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: chave }),
  });
  const pronto = apto && procuradorId && outorgante.trim() && arquivo
    && (validade !== "votacao" || votacaoId) && (validade !== "data" || ate);
  const nomeVotacao = (id: number | null) => votacoes.find((v) => v.id === id)?.titulo ?? `votação ${id}`;
  const fmt = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "");
  const sel = "w-full text-sm rounded-md border border-slate-300 px-2.5 py-1.5 bg-white";

  return (
    <div className="mt-6 bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
          <FileSignature size={16} /> Procurações
        </h3>
        {!aberto && (
          <button onClick={() => { setAberto(true); setErro(null); }}
            className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-md bg-brand-700 text-white">
            <Plus size={13} /> Autorizar inquilino/morador a votar
          </button>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mt-1">
        Só o <b>proprietário</b> vota. Para o inquilino ou outro morador votar no lugar dele, anexe aqui a
        <b> procuração assinada pelo proprietário</b>. O documento fica guardado e a ata registra o voto
        "por procuração". Se o proprietário votar, o voto dele vale.
      </p>

      {aberto && (
        <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="text-xs text-slate-600">Apartamento
            <select className={sel} value={apto} onChange={(e) => escolherApto(e.target.value)}>
              <option value="">Escolha…</option>
              {aptos.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-600">Quem vai votar (procurador)
            <select className={sel} value={procuradorId} onChange={(e) => setProcuradorId(e.target.value)} disabled={!apto}>
              <option value="">{apto && candidatos.length === 0 ? "Cadastre o inquilino/morador no Cad. Acesso" : "Escolha…"}</option>
              {candidatos.map((m) => (
                <option key={m.id} value={m.id}>{m.nome} ({m.relacao === "inquilino" ? "inquilino" : "morador"})</option>
              ))}
            </select>
          </label>
          <label className="text-xs text-slate-600">Proprietário que assinou (outorgante)
            <input className={sel} value={outorgante} onChange={(e) => setOutorgante(e.target.value)}
              list="donos-apto" placeholder="Nome do proprietário" />
            <datalist id="donos-apto">{donos.map((d) => <option key={d.id} value={d.nome} />)}</datalist>
          </label>
          <label className="text-xs text-slate-600">Vale para
            <select className={sel} value={validade} onChange={(e) => setValidade(e.target.value as typeof validade)}>
              <option value="votacao">Uma votação</option>
              <option value="data">Todas, até uma data</option>
              <option value="revogar">Todas, até eu revogar</option>
            </select>
          </label>
          {validade === "votacao" && (
            <label className="text-xs text-slate-600 sm:col-span-2">Votação
              <select className={sel} value={votacaoId} onChange={(e) => setVotacaoId(e.target.value)}>
                <option value="">Escolha…</option>
                {votacoesAbertas.map((v) => <option key={v.id} value={v.id}>{v.titulo}</option>)}
              </select>
            </label>
          )}
          {validade === "data" && (
            <label className="text-xs text-slate-600 sm:col-span-2">Válida até (inclusive)
              <input type="date" className={sel} value={ate} onChange={(e) => setAte(e.target.value)} />
            </label>
          )}
          <label className="text-xs text-slate-600 sm:col-span-2">Documento da procuração (PDF ou foto)
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.heic" className="block w-full text-sm mt-1"
              onChange={(e) => setArquivo(e.target.files?.[0] ?? null)} />
          </label>
          <div className="sm:col-span-2 flex gap-2">
            <button onClick={() => criar.mutate()} disabled={!pronto || criar.isPending}
              className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-md bg-brand-700 text-white disabled:opacity-50">
              {criar.isPending ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Registrar procuração
            </button>
            <button onClick={() => { setAberto(false); setErro(null); }} className="text-xs px-2 py-1.5 text-slate-500">cancelar</button>
          </div>
          {erro && <div className="sm:col-span-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5">{erro}</div>}
        </div>
      )}

      <div className="mt-3 space-y-1.5">
        {(procs ?? []).length === 0 && <p className="text-xs text-slate-400">Nenhuma procuração registrada.</p>}
        {(procs ?? []).map((p) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 text-xs border border-slate-100 rounded-md px-2.5 py-1.5">
            <div className="min-w-0">
              <b>{p.unidade}</b> · {p.procurador_nome} vota por {p.outorgante_nome}
              <span className="text-slate-500"> · {p.votacao_id ? nomeVotacao(p.votacao_id)
                : p.valida_ate ? `até ${fmt(p.valida_ate)}` : "até revogar"}</span>
              {!p.vigente && <span className="ml-1.5 text-[10px] font-bold text-slate-500 bg-slate-100 rounded px-1">{p.revogada_em ? "REVOGADA" : "VENCIDA"}</span>}
            </div>
            <div className="flex items-center gap-2">
              <a href={procuracaoArquivoUrl(p.id)} target="_blank" rel="noreferrer" className="text-brand-700 hover:underline">ver documento</a>
              {p.vigente && (
                <button onClick={() => { if (confirm("Revogar esta procuração?")) revogar.mutate(p.id); }}
                  className="text-rose-600 hover:underline">revogar</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
